import React, { useRef, useState } from 'react';
import { ClearButton, InputTooltipWrapper } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { getCleanNumberInputValue, noop } from '@fabric-ui/utils';
import { Box, Button, Collapse, SxProps, TextField, TextFieldProps, Theme, useTheme } from '@mui/material';
import { TextFieldVariants } from '@mui/material/TextField/TextField';
import { Property } from 'csstype';

const listItemIcon = (activeIcon: boolean, pointerEvents?: Property.PointerEvents) => ({
  ...flex.itemsCenter,
  gap: '2px',
  color: (theme: Theme) => (activeIcon ? theme.palette.colors.primary[500] : theme.palette.colors.neutrals[600]),
  pointerEvents,
  '& .MuiIconButton-root': {
    padding: 0,
    '.MuiOutlinedInput-root.Mui-error &': {
      color: (theme: Theme) => theme.palette.error.main,
    },
    '& svg': {
      height: 16,
      width: 16,
    },
  },
});

export enum EventSourceType {
  UserType = 'userType',
  ClearButton = 'clearButton',
}

export interface TextInputProps extends Omit<TextFieldProps, 'variant'> {
  variant?: TextFieldVariants;
  label?: string | React.ReactNode;
  value?: string | number | null;
  helperText?: string;
  error?: any;
  isRequired?: boolean;
  disabled?: boolean;
  onChange?: any;
  icon?: React.ReactNode;
  disableClearButton?: boolean;
  actionIcons?: React.ReactNode[];
  tooltipContent?: string | React.ReactNode;
  startAdornment?: () => React.ReactNode;
  enableAnimation?: boolean;
  inputProps?: Record<string, any>;
  onBlur?: (e: any, isClear?: boolean) => void;
  onKeyDown?: (e: any) => void;
  readOnly?: boolean;
}
export const TextInput = ({
  label = undefined,
  value = '',
  helperText = '',
  error = '',
  isRequired = false,
  onChange = noop,
  disabled = false,
  icon,
  disableClearButton = false,
  actionIcons = [],
  tooltipContent,
  startAdornment,
  enableAnimation = false,
  inputProps = {},
  ref,
  readOnly,
  ...muiProps
}: TextInputProps) => {
  const innerRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const [isSearchOpen, setIsSearchOpen] = useState(!enableAnimation);
  const OnBlurAnimation = () => !value && setIsSearchOpen(false);
  const iconRef: any = useRef(undefined);
  const toggleAnimation = () => {
    setIsSearchOpen(!isSearchOpen);
    if (!isSearchOpen) {
      iconRef.current.nextSibling.focus();
    }
  };

  const InputProps = {
    startAdornment:
      startAdornment?.() ||
      (icon &&
        (enableAnimation ? (
          <Button ref={iconRef} onClick={toggleAnimation}>
            {icon}
          </Button>
        ) : (
          <Box sx={listItemIcon(!!(icon && value), 'none')}>{icon}</Box>
        ))),
    endAdornment:
      actionIcons.length || (!disableClearButton && value) ? (
        <Box sx={listItemIcon(!!value)}>
          {!disableClearButton && value && (
            <ClearButton
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                onChange('', { source: EventSourceType.ClearButton });
                muiProps.onBlur?.(e, true);
              }}
            />
          )}
          {[...actionIcons]}
        </Box>
      ) : (
        ''
      ),
    readOnly,
    ...inputProps,
  };

  const textField = (
    <TextField
      autoFocus={enableAnimation}
      ref={ref}
      label={label}
      value={value === null ? '' : value}
      required={isRequired}
      disabled={disabled}
      placeholder={disabled ? undefined : helperText}
      error={Boolean(error)}
      onChange={({ target }) =>
        onChange(muiProps.type === 'number' ? getCleanNumberInputValue(target.value) : target.value, { source: EventSourceType.UserType })
      }
      InputProps={InputProps}
      {...muiProps}
      sx={{ ...(readOnly && readOnlyStyle), ...(muiProps?.sx || {}) } as SxProps}
      inputProps={{ ref: innerRef }}
      onBlur={
        enableAnimation
          ? (e: React.FocusEvent<HTMLInputElement>) => {
              muiProps.onBlur?.(e);
              if (e.relatedTarget !== iconRef.current) {
                OnBlurAnimation();
              }
            }
          : muiProps.onBlur
      }
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        muiProps.onKeyDown?.(e);
      }}
    />
  );

  return enableAnimation ? (
    <Collapse
      in={isSearchOpen}
      orientation="horizontal"
      sx={collapseStyle(isSearchOpen, theme)}
      timeout={theme.transitions.duration.short}
      easing="ease-in-out"
      collapsedSize={
        iconRef.current
          ? iconRef.current.offsetWidth + +getComputedStyle(iconRef.current.closest('.MuiInputBase-root')).paddingLeft.slice(0, -2)
          : undefined
      }>
      <InputTooltipWrapper tooltipContent={tooltipContent} error={error} inputNode={innerRef?.current} value={value as any}>
        {textField}
      </InputTooltipWrapper>
    </Collapse>
  ) : (
    <InputTooltipWrapper tooltipContent={tooltipContent} error={error} inputNode={innerRef?.current} value={value as any}>
      {textField}
    </InputTooltipWrapper>
  );
};

const collapseStyle: (isSearchOpen: boolean, theme: Theme) => SxProps = (isSearchOpen, theme) => ({
  ...(isSearchOpen ? undefined : { '.MuiInputBase-root': { pointerEvents: 'none' }, '.MuiButtonBase-root': { pointerEvents: 'all' } }),
  '.MuiOutlinedInput-notchedOutline': {
    opacity: isSearchOpen ? 1 : 0,
    transition: theme.transitions.create(['opacity'], { duration: theme.transitions.duration.short }),
  },
});

const readOnlyStyle: Partial<SxProps<Theme>> = {
  backgroundColor: (theme: Theme) => theme.palette.colors.neutrals[150],
  borderColor: (theme: Theme) => theme.palette.colors.neutrals[400],
  color: (theme: Theme) => theme.palette.colors.neutrals[700],
  pointerEvents: 'none',
};
